import { apiClient } from '@littlebird/auth-client';
import { useQuery, type QueryClient } from '@tanstack/react-query';

type ScreenshotPreviewVariant = 'thumb' | 'preview';

// The backend answers 404 once a preview has expired or been deleted, and
// for every preview while the user has previews turned off
type ScreenshotPreviewImage =
  | { status: 'available'; blob: Blob }
  | { status: 'unavailable' };

const UNAVAILABLE: ScreenshotPreviewImage = { status: 'unavailable' };
const THUMBS_KEY = ['screenshot_previews', 'thumb'];
const thumbKey = (previewId: string) => [...THUMBS_KEY, previewId];

const isAvailable = (
  image: unknown
): image is Extract<ScreenshotPreviewImage, { status: 'available' }> =>
  (image as ScreenshotPreviewImage | undefined)?.status === 'available';

// A 404 can mean the user deleted context, so no other cached thumbnail
// is trusted: the ones no part shows go, and the ones on screen ask again
const dropOtherThumbnails = (queryClient: QueryClient, previewId: string) => {
  const others = {
    queryKey: THUMBS_KEY,
    predicate: (query: {
      queryKey: readonly unknown[];
      state: { data: unknown };
    }) => query.queryKey[2] !== previewId && isAvailable(query.state.data),
  };
  queryClient.removeQueries({ ...others, type: 'inactive' });
  void queryClient.invalidateQueries(others, { cancelRefetch: false });
};

export const fetchScreenshotPreview = async (
  queryClient: QueryClient,
  previewId: string,
  variant: ScreenshotPreviewVariant,
  signal?: AbortSignal
): Promise<ScreenshotPreviewImage> => {
  const response = await apiClient.fetch(
    `screenshot_previews/${encodeURIComponent(previewId)}/${variant}`,
    { cache: 'no-store', signal }
  );
  if (response.status === 404) {
    dropOtherThumbnails(queryClient, previewId);
    return UNAVAILABLE;
  }
  if (!response.ok) {
    throw new Error(
      `Screenshot preview request failed with HTTP ${response.status}`
    );
  }
  return { status: 'available', blob: await response.blob() };
};

export const useScreenshotThumbQuery = (
  queryClient: QueryClient,
  previewId: string
) =>
  useQuery({
    queryKey: thumbKey(previewId),
    queryFn: ({ signal }) =>
      fetchScreenshotPreview(queryClient, previewId, 'thumb', signal),
    // The part refetches on the window's `focus` event instead, which React
    // Query does not listen to
    refetchOnWindowFocus: false,
    // A preview's bytes never change, so a refetch that finds it still there
    // keeps the first blob, and the image on screen never reloads
    structuralSharing: (previous: unknown, next: unknown) =>
      isAvailable(previous) && isAvailable(next) ? previous : next,
  });

export const refetchScreenshotThumb = (
  queryClient: QueryClient,
  previewId: string
) =>
  queryClient.refetchQueries(
    { queryKey: thumbKey(previewId), exact: true },
    { cancelRefetch: false }
  );

export const markScreenshotThumbUnavailable = (
  queryClient: QueryClient,
  previewId: string
) => queryClient.setQueryData(thumbKey(previewId), UNAVAILABLE);
